// utils/offlineStorage/queue.ts - utils/offlineStorage.ts split (sub-module queue)


import type {QueuedOperation} from './queueTypes.js';


export class OperationQueue {
  private readonly queueKey = 'omd_operation_queue';

  /**
   * Add operation to queue
   */
  enqueue(operation: Omit<QueuedOperation, 'id' | 'timestamp'>): void {
    try {
      const queue = this.getQueue();
      const newOperation: QueuedOperation = {
        ...operation,
        id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        timestamp: Date.now(),
      };
      queue.push(newOperation);
      localStorage.setItem(this.queueKey, JSON.stringify(queue));
    } catch (error) {
      console.error('Failed to enqueue operation:', error);
    }
  }

  /**
   * Get all pending operations
   */
  getQueue(): QueuedOperation[] {
    try {
      const queue = localStorage.getItem(this.queueKey);
      return queue ? JSON.parse(queue) : [];
    } catch {
      return [];
    }
  }

  /**
   * Remove operation from queue
   */
  dequeue(id: string): void {
    try {
      const queue = this.getQueue().filter((op) => op.id !== id);
      localStorage.setItem(this.queueKey, JSON.stringify(queue));
    } catch (error) {
      console.error('Failed to dequeue operation:', error);
    }
  }

  /**
   * Clear all pending operations
   */
  clearQueue(): void {
    localStorage.removeItem(this.queueKey);
  }

  /**
   * Get queue length
   */
  get length(): number {
    return this.getQueue().length;
  }

  /**
   * Group queue by table and type for batch processing
   */
  getBatchableGroups(): Map<string, QueuedOperation[]> {
    const queue = this.getQueue();
    const groups = new Map<string, QueuedOperation[]>();

    queue.forEach((op) => {
      const key = `${op.table}_${op.type}`;
      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key)!.push(op);
    });

    return groups;
  }

  /**
   * Get operations by type
   */
  getByType(type: 'create' | 'update' | 'delete'): QueuedOperation[] {
    return this.getQueue().filter((op) => op.type === type);
  }

  /**
   * Get operations by table
   */
  getByTable(table: string): QueuedOperation[] {
    return this.getQueue().filter((op) => op.table === table);
  }
}
